import * as d3 from "d3";
import styled from "styled-components/macro";

import { ROW_HEIGHT } from "../../../constants/constants";
import { FORMAT_TIME_WITH_SECONDS } from "utils/timeUtils";
import {
  useTimeAxisContext,
  useFormatZoned,
  useForceUpdateOnInterval,
} from "utils/customHooks/genericHooks";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { darkModeTextColor } from "constants/colors";
import { useIsDarkMode } from "store/atoms";

const StyledG = styled.g<{
  $isDarkMode: boolean;
}>`
  text {
    font-size: 13px;
    text-anchor: middle;
    font-weight: bold;
    fill: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "black")};
  }
  path {
    stroke: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "black")};
    stroke-width: 2px;
  }
`;

/** display time with concern to the timeZone selected */
const CurrentTime = ({ x }) => {
  const now = useForceUpdateOnInterval({ updateInterval: 200 });
  const formatZoned = useFormatZoned();
  const [isDarkMode] = useIsDarkMode();
  if (isNaN(x)) {
    return null;
  }
  const nowFormatted = formatZoned(new Date(now), FORMAT_TIME_WITH_SECONDS);
  return (
    <g>
      <defs>
        <filter id="shadow" x="0%" y="0%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1 1" result="shadow" />
          <feOffset dx="0" dy="0" />
        </filter>
      </defs>
      {/* a white drop-shadow behind the text, for better visibility on top of ShiftBoundariesIndicators */}
      <text
        x={x}
        y={15}
        style={{ filter: "url(#shadow)" }}
        stroke={isDarkMode ? "#000" : "#fff"}
        strokeWidth={3}
      >
        {nowFormatted}
      </text>
      {/* the text */}
      <text x={x} y={15}>
        {nowFormatted}
      </text>
    </g>
  );
};

interface Props {
  height: number;
  location: number | null;
}

export default function NowIndicator({ height, location }: Props) {
  const [isDarkMode] = useIsDarkMode();
  const isStorybook = useIsStorybook();
  const { scaleTime, xLeft, xRight } = useTimeAxisContext();
  const [tLeft, tRight] = scaleTime.domain();
  const msPerPixel = (+tRight - +tLeft) / (xRight - xLeft);

  const now = useForceUpdateOnInterval({
    updateInterval: Math.max(100, 0.3 * msPerPixel), // Update somewhat faster than whole pixel
  });

  const x = location !== null ? location : scaleTime(now) || 0;

  // If now is not in range, don't render it.
  if (x < xLeft || x > xRight) {
    return null;
  }

  const path = d3.line()([
    [x, 18],
    [x, height + ROW_HEIGHT / 2 + 18],
  ]);
  return isStorybook ? null : (
    <StyledG $isDarkMode={isDarkMode} className="nowIndicator">
      <CurrentTime x={x} />
      <path d={path || ""} />
    </StyledG>
  );
}
